import { FaRobot, FaHistory, FaTrash, FaArrowLeft } from "react-icons/fa";
import { useState, useRef, useEffect } from "react";
import { RxCross2 } from "react-icons/rx";
import { useContext } from "react";
import assistant from "../../assets/img/assistant.png";
import { IoIosSend } from "react-icons/io";
import { MdAttachFile } from "react-icons/md";
import { env } from "../../datas.config";
import { MarkdownTypewriter } from "./MarkdownMessage";
import { WorkspaceContext } from "../../contexts/WorkspaceContext";
import {
  uploadRagDocument,
  getRagFormats,
} from "../../services/workspace-management";
import "./chatbot.css";

const WELCOME_MESSAGE = {
  key: "welcome",
  emetteur: "bot",
  sms: "Bonjour, Je m'appelle Pélagie votre assistante intélligente, que puis-je faire pour vous aujourd'hui?",
  timeUnix: Date.now(),
};

function formatRelativeDate(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "A l'instant";
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  if (diffH < 24) return `Il y a ${diffH}h`;
  if (diffD < 7) return `Il y a ${diffD}j`;
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

const nextTimeUnix = (list) =>
  Math.max(Date.now(), (list[list.length - 1]?.timeUnix || 0) + 1);

const newKey = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export default function Chatbot() {
  const [display, setDisplay] = useState(false);
  const { currentWorkspace } = useContext(WorkspaceContext);
  const workspaceId = currentWorkspace?._id;
  const [allMessages, setAllMessages] = useState([WELCOME_MESSAGE]);
  const [sessionId, setSessionId] = useState(null);
  const [typingKey, setTypingKey] = useState(null);
  const [view, setView] = useState("chat");
  const [conversations, setConversations] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingResponse, setLoadingResponse] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [ragAccept, setRagAccept] = useState("");
  const contentRef = useRef(null);
  const boxRefChatbot = useRef(null);
  const fileInputRef = useRef(null);

  function getFormattedDate() {
    const today = new Date();
    return today.toLocaleDateString("fr-FR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  const fetchHistory = () => {
    setLoadingHistory(true);
    fetch(`${env.domain}/api/auth/conversation/ai-history`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        setConversations(data.conversations || []);
        setLoadingHistory(false);
      })
      .catch(() => setLoadingHistory(false));
  };

  const loadConversation = (id) => {
    fetch(`${env.domain}/api/auth/conversation/ai-history/${id}`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.conversation) {
          setAllMessages(
            data.conversation.messages.map((message) => ({
              ...message,
              key: newKey(),
            }))
          );
          setSessionId(data.conversation._id);
          setTypingKey(null);
          setView("chat");
        }
      })
      .catch((err) => console.error(err));
  };

  const deleteConversation = (id, e) => {
    e.stopPropagation();
    fetch(`${env.domain}/api/auth/conversation/ai-history/${id}`, {
      method: "DELETE",
      credentials: "include",
    })
      .then((res) => res.json())
      .then(() => {
        setConversations((prev) => prev.filter((c) => c._id !== id));
        if (sessionId === id) {
          setSessionId(null);
          setTypingKey(null);
          setAllMessages([WELCOME_MESSAGE]);
        }
      })
      .catch((err) => console.error(err));
  };

  const sendMessage = () => {
    const content = contentRef.current.innerText;
    const contentTrimed = content.trim();
    if (content.length > 0 && contentTrimed !== "") {
      setAllMessages((prevItems) => [
        ...prevItems,
        {
          key: newKey(),
          emetteur: "me",
          sms: contentTrimed,
          timeUnix: nextTimeUnix(prevItems),
        },
      ]);
      contentRef.current.innerText = "";
      setLoadingResponse(true);

      fetch(`${env.domain}/api/auth/conversation/ai-chatbot`, {
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        method: "post",
        body: JSON.stringify({
          chatInput: contentTrimed,
          sessionId,
        }),
      })
        .then((res) => res.json())
        .then((result) => {
          setLoadingResponse(false);
          if (result.conversationId) {
            setSessionId(result.conversationId);
          }
          const botKey = newKey();
          setAllMessages((prevItems) => [
            ...prevItems,
            { ...result.response, key: botKey, timeUnix: nextTimeUnix(prevItems) },
          ]);
          setTypingKey(botKey);
        })
        .catch((erreur) => {
          setLoadingResponse(false);
          console.clear();
          console.error(erreur);
          setAllMessages((prevItems) =>
            [
              ...prevItems,
              {
                key: newKey(),
                sms: "Désolé un problème est survenu, veuillez vérifier l'état de votre connexion internet avant de réessayer.",
                emetteur: "bot",
                timeUnix: nextTimeUnix(prevItems),
              },
            ].sort((a, b) => a.timeUnix - b.timeUnix)
          );
        });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const pushSystemMessage = (text) => {
    setAllMessages((prevItems) => [
      ...prevItems,
      {
        key: newKey(),
        emetteur: "bot",
        sms: text,
        timeUnix: nextTimeUnix(prevItems),
      },
    ]);
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;

    if (!workspaceId) {
      pushSystemMessage(
        "Aucun espace de travail actif : impossible d'ajouter un document."
      );
      return;
    }

    setUploadingDoc(true);
    try {
      const result = await uploadRagDocument(workspaceId, file);
      if (result && (result.status === 201 || result.document)) {
        pushSystemMessage(
          `« ${file.name} » a été ajouté à la base de connaissances de Pélagie.`
        );
      } else {
        pushSystemMessage(
          result?.msg || `Échec de l'ajout de « ${file.name} ».`
        );
      }
    } catch (err) {
      console.error(err);
      pushSystemMessage(`Échec de l'ajout de « ${file.name} ».`);
    } finally {
      setUploadingDoc(false);
    }
  };

  useEffect(() => {
    getRagFormats()
      .then((data) => {
        if (data && data.status === 200 && data.accept) {
          setRagAccept(data.accept);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        boxRefChatbot.current &&
        !boxRefChatbot.current.contains(event.target)
      ) {
        setDisplay(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      id="tynQuickChat"
      className={`tyn-quick-chat ${display ? "active" : ""}`}
      ref={boxRefChatbot}
    >
      <button
        className="tyn-quick-chat-toggle js-toggle-quick d-flex"
        style={{
          height: "50px",
          width: "50px",
        }}
        onClick={() => setDisplay(true)}
      >
        <FaRobot style={{ fontSize: "100%" }} />
      </button>
      <div className="tyn-quick-chat-box">
        <div
          className="tyn-quick-chat-head"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="tyn-media-group">
            <div className="tyn-media tyn-size-rg">
              <img src={assistant} alt="image_de_chatbot" />
            </div>

            <div className="tyn-media-col">
              <div className="tyn-media-row">
                <h6 className="name">Pélagie</h6>
              </div>
              <div className="tyn-media-row has-dot-sap">
                <span className="meta text-success">Active</span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              className="btn btn-icon btn-sm btn-light rounded-pill"
              title="Historique"
              onClick={() => {
                if (view === "history") {
                  setView("chat");
                } else {
                  fetchHistory();
                  setView("history");
                }
              }}
            >
              <FaHistory style={{ fontSize: "14px" }} />
            </button>
          </div>
        </div>

        {view === "history" ? (
          <div className="tyn-quick-chat-reply js-scroll-to-end">
            <div className="p-3" style={{ overflowY: "auto", height: "100%" }}>
              <div className="d-flex align-items-center mb-3">
                <button
                  className="btn btn-sm btn-light rounded-pill me-2"
                  onClick={() => setView("chat")}
                >
                  <FaArrowLeft style={{ fontSize: "12px" }} />
                </button>
                <h6 className="mb-0">Historique des discussions</h6>
              </div>
              {loadingHistory ? (
                <div className="text-center text-muted py-4">Chargement...</div>
              ) : conversations.length === 0 ? (
                <div className="text-center text-muted py-4">
                  Aucune conversation
                </div>
              ) : (
                <ul className="list-group list-group-flush">
                  {conversations.map((conv) => (
                    <li
                      key={conv._id}
                      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center px-2 py-2"
                      style={{ cursor: "pointer" }}
                      onClick={() => loadConversation(conv._id)}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          className="fw-medium text-truncate"
                          style={{ fontSize: "13px" }}
                        >
                          {conv.titre || "Sans titre"}
                        </div>
                        <small
                          className="text-muted"
                          style={{ fontSize: "11px" }}
                        >
                          {formatRelativeDate(conv.updatedAt || conv.createdAt)}
                        </small>
                      </div>
                      <button
                        className="btn btn-sm btn-outline-danger rounded-pill ms-2"
                        style={{ padding: "2px 6px" }}
                        title="Supprimer"
                        onClick={(e) => deleteConversation(conv._id, e)}
                      >
                        <FaTrash style={{ fontSize: "11px" }} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : (
          <div className="tyn-quick-chat-reply js-scroll-to-end">
            <div
              className="tyn-reply box-messages-container"
              id="tynQuickReply"
            >
              {loadingResponse && (
                <div className="tyn-reply-item incoming">
                  <div className="tyn-reply-avatar">
                    <div className="tyn-media tyn-size-md tyn-circle">
                      <img src={assistant} alt="assistant" />
                    </div>
                  </div>
                  <div className="tyn-reply-group">
                    <div
                      className="tyn-reply-bubble"
                      style={{ maxWidth: "100%" }}
                    >
                      <div className="tyn-reply-text">
                        <span className="pelagie-thinking">
                          <span className="pelagie-thinking__label">
                            Pélagie réfléchit
                          </span>
                          <span
                            className="pelagie-thinking__dots"
                            aria-hidden="true"
                          >
                            <span></span>
                            <span></span>
                            <span></span>
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {[...allMessages]
                .sort((a, b) => b.timeUnix - a.timeUnix)
                .map((sms) =>
                  sms.emetteur === "me" ? (
                    <div className="tyn-reply-item outgoing" key={sms.key}>
                      <div className="tyn-reply-group">
                        <div
                          className="tyn-reply-bubble"
                          style={{ maxWidth: "100%" }}
                        >
                          <div className="tyn-reply-text">{sms.sms}</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="tyn-reply-item incoming" key={sms.key}>
                      <div className="tyn-reply-avatar">
                        <div className="tyn-media tyn-size-md tyn-circle">
                          <img src={assistant} alt="assistant" />
                        </div>
                      </div>
                      <div className="tyn-reply-group">
                        <div
                          className="tyn-reply-bubble"
                          style={{ maxWidth: "100%" }}
                        >
                          <div className="tyn-reply-text">
                            <MarkdownTypewriter
                              text={sms.sms || ""}
                              animate={sms.key === typingKey}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )}

              <div className="tyn-reply-separator text-capitalize">
                {getFormattedDate()}
              </div>
            </div>
          </div>
        )}

        <div className="tyn-quick-chat-form">
          <div
            className="tyn-chat-form-input bg-light"
            id="tynQuickChatInput"
            onKeyDown={handleKeyDown}
            contentEditable={!loadingResponse}
            ref={contentRef}
            data-placeholder="Écrivez votre message..."
          ></div>
          <ul className="tyn-list-inline me-n2 my-1 pelagie-input-actions">
            <li>
              <button
                type="button"
                className="pelagie-icon-btn"
                title="Joindre un document à la base de connaissances de Pélagie"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingDoc || loadingResponse}
              >
                {uploadingDoc ? (
                  <span className="pelagie-spinner" />
                ) : (
                  <MdAttachFile style={{ fontSize: "20px" }} />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ragAccept}
                style={{ display: "none" }}
                onChange={handleFileSelected}
              />
            </li>
            <li>
              <button
                className="btn btn-icon btn-white btn-sm btn-pill"
                onClick={() => sendMessage()}
                disabled={loadingResponse}
              >
                <IoIosSend
                  style={{
                    height: "auto",
                    width: "auto",
                    fontSize: "20px",
                    opacity: loadingResponse ? 0.5 : 1,
                  }}
                />
              </button>
            </li>
          </ul>
        </div>

        <button
          className="btn btn-danger btn-sm btn-icon top-0 end-0 position-absolute rounded-pill translate-middle js-toggle-quick"
          onClick={() => setDisplay(false)}
        >
          <RxCross2 />
        </button>
      </div>
    </div>
  );
}
